import React, { useRef, useState } from "react";
import usePlayer, { player } from "../../hooks/usePlayer";
import { formatDuration } from "./PlaylistPage";
import {
  ConnectIcon,
  FullScreenIcon,
  LyricsIcon,
  MiniPlayerIcon,
  MuteIcon,
  NextIcon,
  PauseIcon,
  PlayIcon,
  PrevIcon,
  QueueIcon,
  RepeatIcon,
  RepeatOneIcon,
  SavedIcon,
  ShuffleIcon,
  VolumeIcon,
} from "./icons";

// Spotify's thin slider: drag or click anywhere on it; the fill turns green while you hover. With
// `live`, it changes as you drag (volume); otherwise when you let go (seeking).
const Slider = ({ value, max, onChange, label, valueText, live, className = "" }) => {
  const ref = useRef(null);
  const [drag, setDrag] = useState(null);
  const at = (e) => {
    const r = ref.current.getBoundingClientRect();
    return Math.min(max, Math.max(0, ((e.clientX - r.left) / r.width) * max));
  };
  const shown = drag === null ? value : drag;
  const percent = max ? (shown / max) * 100 : 0;
  return (
    <div
      ref={ref}
      className={`sp-slider ${drag !== null ? "is-dragging" : ""} ${className}`}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
      aria-valuetext={valueText}
      onPointerDown={(e) => {
        if (!max) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setDrag(at(e));
        if (live) onChange(at(e));
      }}
      onPointerMove={(e) => {
        if (drag === null) return;
        setDrag(at(e));
        if (live) onChange(at(e));
      }}
      onPointerUp={(e) => {
        if (drag === null) return;
        onChange(at(e));
        setDrag(null);
      }}
      onKeyDown={(e) => {
        const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
        if (!step) return;
        e.preventDefault();
        onChange(Math.min(max, Math.max(0, value + (step * max) / 20)));
      }}
    >
      <span className="sp-slider-track">
        <span className="sp-slider-fill" style={{ width: `${percent}%` }} />
      </span>
      <span className="sp-slider-thumb" style={{ left: `${percent}%` }} />
    </div>
  );
};

// The bar along the bottom: the song (its cover shows or hides Now Playing), the controls and
// progress, and the volume.
const PlayerBar = ({ nowPlaying, onToggleNowPlaying }) => {
  const { track, playlist, playing, time, duration, volume, shuffle, repeat } = usePlayer();
  const unmuted = useRef(0.5);
  const saved = track.saved || playlist.id === "liked";

  const toggleMute = () => {
    if (volume > 0) {
      unmuted.current = volume;
      player.setVolume(0);
    } else player.setVolume(unmuted.current || 0.5);
  };

  return (
    <footer className="sp-bar">
      <div className="sp-bar-song">
        <button
          type="button"
          className="sp-bar-cover-button"
          onClick={onToggleNowPlaying}
          aria-label={nowPlaying ? "Hide Now Playing view" : "Show Now Playing view"}
          aria-pressed={nowPlaying}
        >
          <img src={track.image} alt="" className="sp-bar-cover" draggable={false} />
        </button>
        <span className="sp-bar-text">
          <span className="sp-bar-title">{track.title}</span>
          <span className="sp-bar-artist">{track.artist}</span>
        </span>
        {saved && <SavedIcon className="sp-saved" aria-label="Saved" />}
      </div>

      <div className="sp-bar-center">
        <div className="sp-controls">
          <button
            type="button"
            className={`sp-control ${shuffle ? "is-on" : ""}`}
            onClick={player.toggleShuffle}
            aria-label={shuffle ? "Disable shuffle" : "Enable shuffle"}
            aria-pressed={shuffle}
          >
            <ShuffleIcon />
          </button>
          <button type="button" className="sp-control" onClick={player.previous} aria-label="Previous">
            <PrevIcon />
          </button>
          <button type="button" className="sp-play" onClick={player.toggle} aria-label={playing ? "Pause" : "Play"}>
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
          <button type="button" className="sp-control" onClick={player.next} aria-label="Next">
            <NextIcon />
          </button>
          <button
            type="button"
            className={`sp-control ${repeat !== "off" ? "is-on" : ""}`}
            onClick={player.cycleRepeat}
            aria-label={{ off: "Enable repeat", context: "Enable repeat one", track: "Disable repeat" }[repeat]}
          >
            {repeat === "track" ? <RepeatOneIcon /> : <RepeatIcon />}
          </button>
        </div>
        <div className="sp-progress">
          <span className="sp-time">{formatDuration(time)}</span>
          <Slider
            value={time}
            max={duration || 0}
            onChange={player.seek}
            label="Change progress"
            valueText={`${formatDuration(time)} of ${formatDuration(duration || 0)}`}
          />
          <span className="sp-time">{duration ? formatDuration(duration) : "-:--"}</span>
        </div>
      </div>

      <div className="sp-bar-right">
        <span className="sp-control" aria-hidden="true">
          <LyricsIcon />
        </span>
        <span className="sp-control" aria-hidden="true">
          <QueueIcon />
        </span>
        <span className="sp-control" aria-hidden="true">
          <ConnectIcon />
        </span>
        <button type="button" className="sp-control" onClick={toggleMute} aria-label={volume > 0 ? "Mute" : "Unmute"}>
          {volume > 0 ? <VolumeIcon /> : <MuteIcon />}
        </button>
        <Slider
          value={volume}
          max={1}
          onChange={player.setVolume}
          label="Change volume"
          valueText={`${Math.round(volume * 100)}%`}
          live
          className="sp-volume"
        />
        <span className="sp-control" aria-hidden="true">
          <MiniPlayerIcon />
        </span>
        <span className="sp-control" aria-hidden="true">
          <FullScreenIcon />
        </span>
      </div>
    </footer>
  );
};

export default PlayerBar;
